import type { ILegalLinks } from '@featit/shared';
import { LegalLink } from './LegalLink';
import { useLegalLinks } from './useLegalLinks';
import { createElement, type ReactNode } from 'react';
import { useT } from '@/i18n/LocaleProvider';

/**
 * A legal link inside a sentence. On a phone it is pressed over 44 px: its padding reaches 44 px tall,
 * and 6 px each side, taken back by an equal negative margin, makes a one-word link ("Terms",
 * 33.9 px of text) at least 44 px wide without moving a letter of the sentence (I-1754's sweep).
 * `relative` is what makes the padding a target (I-1769): an unpositioned inline box paints under the
 * sentence's later words, so a press on the word beside it found the paragraph, 40 px of the 44. The
 * padding is empty, so lifting it above the words hides none of them. It is `inline-block` with
 * its vertical padding taken back by `-my-[14px]` (I-2018, AC-11 of
 * `docs/concept-mobile-web-and-phone-apps.md`): WebKit does not hit-test an inline box's vertical
 * padding, so a press above the word found the paragraph.
 */
export const inlineLegalLinkClasses =
  'relative inline-block rounded py-[14px] max-sm:-mx-[6px] max-sm:-my-[14px] max-sm:px-[6px] font-medium evx-link transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:py-0 ';

const FOOTER_LINK =
  'inline-flex min-h-touch min-w-touch items-center justify-center rounded px-1 text-[12px] text-gray-500 transition-colors duration-150 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:min-h-0 sm:min-w-0 dark:text-gray-400 dark:hover:text-gray-100';

/**
 * F-0554, AC-TA9: Imprint, Privacy and Terms under a signed-out screen — § 5 DDG wants the imprint
 * reachable from the apps, and Art. 13 GDPR the privacy information at sign-up. Draws nothing until
 * the deployment names its website: three links to nowhere are worse than none.
 */
export function LegalLinks(props: { links?: ILegalLinks | null }) {
  return 'links' in props ? <LegalLinksNav links={props.links} /> : <LoadedLegalLinks />;
}

function LoadedLegalLinks() {
  return <LegalLinksNav links={useLegalLinks()} />;
}

function LegalLinksNav({ links }: { links: ILegalLinks | null | undefined }) {
  const t = useT();
  if (!links) return null;
  return (
    <nav
      aria-label={t('auth.legal.navLabel')}
      data-testid="legal-links"
      className="mt-6 flex items-center justify-center gap-3 sm:gap-4"
    >
      <LegalLink href={links.imprint} className={FOOTER_LINK}>
        {t('auth.legal.imprint')}
      </LegalLink>
      <LegalLink href={links.privacy} className={FOOTER_LINK}>
        {t('auth.legal.privacy')}
      </LegalLink>
      <LegalLink href={links.terms} className={FOOTER_LINK}>
        {t('auth.legal.terms')}
      </LegalLink>
    </nav>
  );
}

/**
 * F-0554, AC-TA1: the sentence under *Create account*. Pressing the button is the acceptance the
 * sign-up records, so the sentence names exactly what is accepted and links both pages.
 */
export function TermsAcceptanceLine({ links }: { links: ILegalLinks | null | undefined }) {
  const t = useT();
  // The sentence's pieces are spread as the paragraph's children, not handed over as one list: a
  // list asks every piece for a key, and the dev /register logged that warning for this line.
  const sentence = t.rich('auth.legal.acceptanceLine', {
    terms: (chunks: ReactNode) => (
      <LegalLink href={links?.terms} className={inlineLegalLinkClasses}>
        {chunks}
      </LegalLink>
    ),
    privacy: (chunks: ReactNode) => (
      <LegalLink href={links?.privacy} className={inlineLegalLinkClasses}>
        {chunks}
      </LegalLink>
    ),
  }) as ReactNode[];
  return createElement(
    'p',
    {
      'data-testid': 'terms-acceptance-line',
      className: 'text-[12px] leading-relaxed text-gray-500 dark:text-gray-400',
    },
    ...sentence,
  );
}
